/**
 * /profile/resume-studio — AI-assisted resume editor.
 *
 * Flow:
 *   1. Load the user's profile.
 *   2. User clicks "Generate full draft with AI" (or a per-section wand).
 *   3. Backend `/profile/resume/ai-suggest` returns polished text — presented
 *      side-by-side with the original.
 *   4. User edits inline, then Save (persists to profile) or Download PDF.
 *
 * All AI calls preserve facts (dates, company names, etc.) — the system prompt
 * on the backend enforces this. The user can always fall back to the original.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Sparkles, Save, Download, ChevronLeft, Loader2, RotateCcw, Wand2, Info,
} from "lucide-react";

export default function ResumeStudio() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tone, setTone] = useState("professional");
  const [genAll, setGenAll] = useState(false);
  const [genSection, setGenSection] = useState(null); // "about" | "career" | ...

  // Editable working copy of the resume fields.
  const [draft, setDraft] = useState({ about: "", career: [], education: [], skills: [], achievements: [] });
  // Snapshot of the original loaded values so the user can Revert.
  const [orig, setOrig] = useState({ about: "", career: [], education: [], skills: [], achievements: [] });

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await http.get("/me/profile");
        const u = data.user || {};
        const seed = {
          about: u.about || "",
          career: (u.career || []).map((c) => ({ ...c })),
          education: (u.education || []).map((e) => ({ ...e })),
          skills: (u.skills || []).map((s) => ({ ...s })),
          achievements: (u.achievements || []).map((a) => ({ ...a })),
        };
        setMe(u);
        setDraft(seed);
        setOrig(JSON.parse(JSON.stringify(seed)));
      } catch (e) {
        toast.error("Couldn't load your profile.");
      } finally { setLoading(false); }
    })();
  }, [user]);

  const generateWithAI = async (sections /* array | null for all */) => {
    const isAll = !sections || sections.length === 0;
    if (isAll) setGenAll(true); else setGenSection(sections[0]);
    try {
      const { data } = await http.post("/profile/resume/ai-suggest", { sections: sections || [], tone });
      const d = data.draft || {};
      setDraft((cur) => ({
        about: d.about !== undefined ? d.about : cur.about,
        career: d.career ? mergeById(cur.career, d.career, ["description"]) : cur.career,
        education: d.education ? mergeById(cur.education, d.education, ["notes"]) : cur.education,
        skills: d.skills ? mergeById(cur.skills, d.skills, ["description"]) : cur.skills,
        achievements: d.achievements ? mergeById(cur.achievements, d.achievements, ["description"]) : cur.achievements,
      }));
      toast.success(isAll ? "AI draft ready — review, edit, then save." : `AI updated ${sections[0]}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI drafting failed. Try again in a minute.";
      toast.error(msg);
    } finally {
      setGenAll(false);
      setGenSection(null);
    }
  };

  const revertSection = (key) => setDraft((cur) => ({ ...cur, [key]: JSON.parse(JSON.stringify(orig[key])) }));

  const saveAll = async () => {
    setSaving(true);
    try {
      // Order matters only for UX (progress feedback). Ignore individual failures per section.
      await http.patch("/profile/extended", { about: draft.about });
      if (draft.career.length)       await http.patch("/profile/career",       { items: draft.career });
      if (draft.education.length)    await http.patch("/profile/education",    { items: draft.education });
      if (draft.skills.length)       await http.patch("/profile/skills",       { items: draft.skills });
      if (draft.achievements.length) await http.patch("/profile/achievements", { items: draft.achievements });
      toast.success("Resume saved to your profile.");
      setOrig(JSON.parse(JSON.stringify(draft)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  const downloadPDF = () => {
    if (!me?.handle) { toast.error("Set a public handle in Profile Edit first."); return; }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/profile/resume/${me.handle}.pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const busy = genAll || !!genSection;

  if (!user) return <div className="max-w-4xl mx-auto px-6 py-24 text-center" style={{ color: "var(--wofy-text-soft)" }}>Please sign in.</div>;
  if (loading || !me) return <div className="max-w-4xl mx-auto px-6 py-24 text-center inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="resume-studio-page">
      <Link to="/profile" className="inline-flex items-center gap-2 text-sm hover:underline mb-4" style={{ color: "var(--wofy-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to my profile
      </Link>
      <div className="overline">SOCIAL PULSE · AI RESUME STUDIO</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2">Resume Studio</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Let AI polish your profile into a resume. All your facts (dates, company names, awards) are preserved — the AI only rewrites the prose. Review every section, tweak it, save, then download the PDF.
      </p>

      {/* Toolbar */}
      <div className="wofy-card !p-4 mt-6 flex flex-wrap items-center gap-3">
        <label className="text-xs font-mono uppercase tracking-wider opacity-70">Tone</label>
        <select
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          className="wofy-input !py-1 !text-sm"
          data-testid="resume-tone"
        >
          <option value="professional">Professional</option>
          <option value="warm">Warm & personable</option>
          <option value="concise">Concise & punchy</option>
        </select>
        <button
          onClick={() => generateWithAI(null)}
          disabled={busy}
          className="wofy-btn-primary text-sm"
          data-testid="resume-generate-all"
        >
          {genAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          Generate full draft with AI
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={saveAll} disabled={saving || busy} className="wofy-btn-ghost text-sm" data-testid="resume-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save to profile
          </button>
          <button onClick={downloadPDF} className="wofy-btn-primary text-sm" data-testid="resume-download-pdf">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      <div className="mt-3 text-xs inline-flex items-center gap-2 opacity-70">
        <Info className="w-3.5 h-3.5" /> The PDF is generated from your saved profile, so click Save before Download for the latest text.
      </div>

      {/* Sections */}
      <Section
        title="About"
        onGenerate={() => generateWithAI(["about"])}
        onRevert={() => revertSection("about")}
        genLoading={genSection === "about"}
        busy={busy}
      >
        <textarea
          value={draft.about}
          onChange={(e) => setDraft((d) => ({ ...d, about: e.target.value }))}
          className="wofy-input w-full min-h-[140px]"
          placeholder="Short bio — the AI can seed one from your career + skills."
          data-testid="resume-about-input"
        />
      </Section>

      <ListSection
        title="Career / Experience"
        items={draft.career}
        onChangeItem={(idx, v) => setDraft((d) => ({ ...d, career: d.career.map((x, i) => i === idx ? v : x) }))}
        onGenerate={() => generateWithAI(["career"])}
        onRevert={() => revertSection("career")}
        genLoading={genSection === "career"}
        busy={busy}
        empty="Add jobs on Profile Edit → Career."
        renderItem={(c, update) => (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-medium">{c.name}</div>
              <div className="font-mono text-xs opacity-60">{c.from_ym || ""} — {c.to_ym || "Present"}</div>
            </div>
            <textarea
              value={c.description || ""}
              onChange={(e) => update({ ...c, description: e.target.value })}
              className="wofy-input w-full min-h-[80px] mt-2"
              placeholder="Description of your role & impact."
              data-testid={`resume-career-desc-${c.id}`}
            />
          </>
        )}
      />

      <ListSection
        title="Education"
        items={draft.education}
        onChangeItem={(idx, v) => setDraft((d) => ({ ...d, education: d.education.map((x, i) => i === idx ? v : x) }))}
        onGenerate={() => generateWithAI(["education"])}
        onRevert={() => revertSection("education")}
        genLoading={genSection === "education"}
        busy={busy}
        empty="Add education on Profile Edit → Education."
        renderItem={(e, update) => (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-medium">{e.school}</div>
              <div className="font-mono text-xs opacity-60">{e.from_ym || ""} — {e.to_ym || "Present"}</div>
            </div>
            {e.area && <div className="text-xs opacity-70 mt-0.5">{e.area}</div>}
            <textarea
              value={e.notes || ""}
              onChange={(v) => update({ ...e, notes: v.target.value })}
              className="wofy-input w-full min-h-[60px] mt-2"
              placeholder="Notes / achievements at this school."
              data-testid={`resume-edu-notes-${e.id}`}
            />
          </>
        )}
      />

      <ListSection
        title="Skills"
        items={draft.skills}
        onChangeItem={(idx, v) => setDraft((d) => ({ ...d, skills: d.skills.map((x, i) => i === idx ? v : x) }))}
        onGenerate={() => generateWithAI(["skills"])}
        onRevert={() => revertSection("skills")}
        genLoading={genSection === "skills"}
        busy={busy}
        empty="Add skills on Profile Edit → Skills."
        renderItem={(s, update) => (
          <>
            <div className="font-medium">{s.name}</div>
            <input
              value={s.description || ""}
              onChange={(v) => update({ ...s, description: v.target.value })}
              className="wofy-input w-full mt-2 text-sm"
              placeholder="Short one-liner about this skill."
              data-testid={`resume-skill-desc-${s.id}`}
            />
          </>
        )}
      />

      <ListSection
        title="Achievements & Awards"
        items={draft.achievements}
        onChangeItem={(idx, v) => setDraft((d) => ({ ...d, achievements: d.achievements.map((x, i) => i === idx ? v : x) }))}
        onGenerate={() => generateWithAI(["achievements"])}
        onRevert={() => revertSection("achievements")}
        genLoading={genSection === "achievements"}
        busy={busy}
        empty="Add achievements on Profile Edit → Achievements."
        renderItem={(a, update) => (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-medium">{a.title}</div>
              <div className="font-mono text-xs opacity-60">{a.year}{a.month ? `-${String(a.month).padStart(2, "0")}` : ""}</div>
            </div>
            <textarea
              value={a.description || ""}
              onChange={(v) => update({ ...a, description: v.target.value })}
              className="wofy-input w-full min-h-[60px] mt-2"
              placeholder="1-2 sentences about the achievement."
              data-testid={`resume-ach-desc-${a.id}`}
            />
          </>
        )}
      />

      {/* Sticky bottom action bar */}
      <div className="wofy-card !p-4 mt-8 flex items-center justify-end gap-2">
        <button onClick={saveAll} disabled={saving || busy} className="wofy-btn-ghost text-sm" data-testid="resume-save-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save to profile
        </button>
        <button onClick={downloadPDF} className="wofy-btn-primary text-sm" data-testid="resume-download-pdf-2">
          <Download className="w-4 h-4" /> Download PDF
        </button>
      </div>
    </div>
  );
}

// ---------- Reusable section wrappers ----------

function Section({ title, onGenerate, onRevert, genLoading, busy, children }) {
  return (
    <section className="wofy-card !p-5 mt-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-display text-2xl">{title}</h2>
        <div className="flex items-center gap-2">
          <button onClick={onRevert} disabled={busy} className="wofy-btn-ghost text-xs" title="Revert to loaded original">
            <RotateCcw className="w-3.5 h-3.5" /> Revert
          </button>
          <button onClick={onGenerate} disabled={busy} className="wofy-btn-primary text-xs" title="Rewrite this section with AI">
            {genLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Improve with AI
          </button>
        </div>
      </div>
      {children}
    </section>
  );
}

function ListSection({ title, items, onChangeItem, onGenerate, onRevert, genLoading, busy, empty, renderItem }) {
  return (
    <Section title={title} onGenerate={onGenerate} onRevert={onRevert} genLoading={genLoading} busy={busy}>
      {items.length === 0 ? (
        <div className="text-sm opacity-60">{empty}</div>
      ) : (
        <div className="space-y-4">
          {items.map((it, idx) => (
            <div key={it.id || idx} className="border rounded-lg p-3" style={{ borderColor: "var(--wofy-border)" }}>
              {renderItem(it, (updated) => onChangeItem(idx, updated))}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ---------- Utility: merge AI-updated fields into current items by id ----------

function mergeById(currentItems, updatedItems, fields) {
  const byId = new Map((updatedItems || []).map((u) => [u.id, u]));
  return currentItems.map((c) => {
    const u = byId.get(c.id);
    if (!u) return c;
    const merged = { ...c };
    for (const f of fields) if (u[f] !== undefined) merged[f] = u[f];
    return merged;
  });
}
